export type HighlightVariant = 'focus' | 'hover';

export interface Highlighter {
  show: (element: Element) => void;
  hide: () => void;
}

/** Below the "Leave draft mode" link (999999), above page content. */
const Z_INDEX = '999998';

const BRAND = '34, 118, 252';

const styles: Record<HighlightVariant, Partial<CSSStyleDeclaration>> = {
  // Where the editor is: a solid outline, no fill, so the content stays readable.
  focus: { outline: `2px solid rgb(${BRAND})`, background: 'transparent' },
  // Where the pointer is: lighter, so it reads as provisional and never competes with focus.
  hover: { outline: `2px dashed rgb(${BRAND})`, background: `rgba(${BRAND}, 0.08)` },
};

/**
 * One outline box that follows an element.
 *
 * A fixed-position box in `document.body` rather than a class or style on the element itself: it must
 * not touch the page's own markup, or its layout, or anything the overlay measures. `pointer-events:
 * none` keeps it out of every hit test, so it can never swallow a click or alter which element the
 * overlay thinks is hovered.
 *
 * It tracks the target's rect every frame while shown, which covers scrolling, resizing, and layout
 * that moves for reasons of its own, without listening for any of them. That is cheap because at most
 * one box of each variant exists, and only while the Studio is pointing at something.
 */
export const createHighlighter = (variant: HighlightVariant): Highlighter => {
  let box: HTMLDivElement | undefined;
  let target: Element | undefined;
  let frame: number | undefined;

  const place = (): void => {
    if (!box || !target) return;
    if (!target.isConnected) {
      hide();
      return;
    }
    const rect = target.getBoundingClientRect();
    Object.assign(box.style, {
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
  };

  const track = (): void => {
    place();
    frame = target ? window.requestAnimationFrame(track) : undefined;
  };

  const hide = (): void => {
    target = undefined;
    if (frame !== undefined) window.cancelAnimationFrame(frame);
    frame = undefined;
    box?.remove();
    box = undefined;
  };

  const show = (element: Element): void => {
    target = element;
    if (!box) {
      box = document.createElement('div');
      box.dataset.enecoStudioHighlight = variant;
      box.setAttribute('aria-hidden', 'true');
      Object.assign(box.style, {
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: Z_INDEX,
        boxSizing: 'border-box',
        outlineOffset: '2px',
        borderRadius: '2px',
        ...styles[variant],
      });
      document.body.append(box);
    }
    // Placed at once rather than on the next frame, so there is no flash at the corner of the page.
    place();
    frame ??= window.requestAnimationFrame(track);
  };

  return { show, hide };
};
